



    // Vue
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { Mixins } from 'vue-mixin-decorator';
    import { mapGetters } from 'vuex';

    // Third party
    import dayjs, { UnitType } from 'dayjs';
    import 'dayjs/locale/es';

    // Constants
    import Constants from '@/constants/constants.ts';

    // Components
    import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
    import Input, {
        InputType,
        InputStyle,
        InputSize,
    } from '@/shared/components/input.component.vue';
    import Btn, { BtnColor, BtnWidth, BtnPredefinedStyle } from '@/shared/components/btn.component.vue';
    import InlineSpinner, { SpinnerSize } from '@/shared/components/inline-spinner.component.vue';
    import Tooltip, { TooltipColor } from '@/shared/components/Tooltip.component.vue';
    import Modal, {
    ModalTitleColor,
    } from "@/shared/components/Modal.component.vue";
    // Views
    import BudgetFeeRow, { FeeType } from '@/manager/budget/views/budget-row/budget-fees/budget-fee-row/index.vue';

    // Models
    import { Budget } from '@/manager/budget/models/budget.model';
    import { BudgetFee, BudgetFeeStatus, BudgetFeePaymentMethod } from '@/manager/budget/models/budget-fee.model';

    // Services
    import BudgetService from '@/manager/budget/budget.service';

    // Filters
    import formatDate from '@/shared/filters/formatDate.filter.ts';
import RouteNames from '@/constants/route-names';


    @Component({
        components: {
            Btn,
            BudgetFeeRow,
            Icon,
            InlineSpinner,
            Input,
            Tooltip,
            Modal,
        },
        computed: {
            ...mapGetters({
                getBudget: 'getBudget',
            }),
        },
        filters: {
            formatDate,
        },
    })
    export default class BudgetFees extends Vue {
        @Prop() private budgetId!: string;
        @Prop() private oddRow!: boolean;


        private iconEnum = IconEnum;
        private feeType = FeeType;
        private editing = false;
        private tooltipColor = TooltipColor;
        private inputType = InputType;
        private inputSize = InputSize;
        private btnWidth = BtnWidth;
        private btnColor = BtnColor;
        private spinnerSize = SpinnerSize;
        private modalTitleColor = ModalTitleColor;
        private sendAdminEmailModalOpened = false;
        private getBudget!: any;
        private editingBudgetFees = false;
        private numberOfFees = 0;
        private requestPending = false;
        private serverError = '';
        private participantList: any[] = [];


        private created() {
            this.budgetParticipant();
        }


        get budget() {
            return this.getBudget(this.budgetId);
        }

        get budgetHasFees() {
            return this.budget.budgetFee && this.budget.budgetFee.length > 0;
        }

        get hasServerError() {
            return this.serverError !== '';
        }

        get showNumberOfFeeInput() {
            return !this.budgetHasFees && !this.editing || this.editingBudgetFees;
        }

        get btnFeesText() {
            let text = 'Generar';
            if ( this.budgetHasFees ) {
                text = 'Editar cuotas';
                if ( this.editing ) {
                    text = 'Guardar';
                }
            } else if ( this.editing ) {
                text = 'Guardar';
            }
            return text;
        }

        get reservationFee() {
            const reservationFee = new BudgetFee();
            reservationFee.status = BudgetFeeStatus.Paid;
            reservationFee.paymentDate = this.budget.created;
            reservationFee.remittanceDate = '';
            reservationFee.paymentMethod = '';
            reservationFee.collectionDate = '';
            reservationFee.price = this.budget.totalPrepaidPrice;
            return reservationFee;
        }

        get soonPaymentFee() {
            const soonPaymentFee = new BudgetFee();
            soonPaymentFee.status = BudgetFeeStatus.Paid;
            soonPaymentFee.paymentDate = undefined;
            soonPaymentFee.remittanceDate = '';
            soonPaymentFee.paymentMethod = '';
            soonPaymentFee.collectionDate = '';
            soonPaymentFee.price = Number(`-${this.budget.discountSoonPayment}`);
            return soonPaymentFee;
        }

        get moneyFlowDiscountFee() {
            const moneyFlowDiscountFee = new BudgetFee();
            moneyFlowDiscountFee.status = BudgetFeeStatus.Paid;
            moneyFlowDiscountFee.paymentDate = this.budget.created;
            moneyFlowDiscountFee.remittanceDate = '';
            moneyFlowDiscountFee.paymentMethod = '';
            moneyFlowDiscountFee.collectionDate = '';
            moneyFlowDiscountFee.price = this.budget.moneyFlowDiscount;
            return moneyFlowDiscountFee;
        }

        get hasSoonPayment() {
            return this.budget.discountSoonPayment > 0;
        }

        get hasMoneyFlowDiscount() {
            return this.budget.moneyFlowDiscount > 0;
        }

        get hasParticipantList() {
            return this.participantList.length > 0;
        }

        private routerLinkToConvocation(participant:any) {
            const route = {
            name: RouteNames.ConvocationDetail,
            params: {
                id: participant.convocation,
            },
            query: { 
                "search-string": this.budget.client.user.fullName, 
            }, 
            };
            return route;
        }


        private budgetParticipant() {
            BudgetService.budgetParticipantList(this.budgetId)
            .then(
                (res) => {
                    const data = res.data.results;
                    this.participantList = data;
                },
                (err) => {
                    this.setServerError(err);
                },
            );
        }

        private btnFeeAction() {
            if ( !this.budgetHasFees && !this.editing ) {
                this.generateFees();
            } else if ( this.editing ) {
                this.toggleSendAdminEmailModalOpened(true)
            } else {
                this.editing = true;
            }
        }

        private toggleSendAdminEmailModalOpened(value: boolean) {
            this.sendAdminEmailModalOpened = value;
        }

        private setServerError(err: any) {
            const serverError = err.response.data;
            const response = err.response;
            this.requestPending = false;
            if (
                response.status === 500 ||
                (
                    response.status === 400 &&
                    serverError.nonFieldErrors !== undefined
                )
            ) {
                this.serverError = Constants.internalServerErrorMessage;
            }
        }

        private addFee() {
            const budgetFees = this.budget.budgetFee;
            const lastFee = budgetFees[budgetFees.length - 1];
            const newFee = new BudgetFee();
            newFee.remittanceDate = '';
            newFee.price = undefined;
            newFee.editing = true;
            newFee.status = BudgetFeeStatus.Pending;
            newFee.budget = this.budgetId;
            if ( lastFee ) {
                newFee.paymentMethod = lastFee.paymentMethod;
                const paymentDateDayJs = dayjs(lastFee.paymentDate).add(1, 'month');
                newFee.paymentDate = paymentDateDayJs.toISOString();
            } else {
                newFee.paymentMethod = '';
                newFee.paymentDate = dayjs().toISOString();
            }
            budgetFees.push(newFee);
            this.$store.dispatch('updateStoreBudget', this.budget);
        }

        private cleanBudget() {
            if ( !this.budget.creditCard ) {
                delete this.budget.creditCard;
            }
            if ( !this.budget.bankAccount ) {
                delete this.budget.bankAccount;
            }
            delete this.budget.bill;
        }

        private saveBudget(sendEmail:boolean = false) {
            this.toggleSendAdminEmailModalOpened(false)
            this.requestPending = true;
            this.cleanBudget();
            this.$store.dispatch('updateBudget', { ...this.budget, send_email: sendEmail }).then(
                (res) => {
                    this.requestPending = false;
                    this.editing = false;
                },
            ).catch((err) => {
                this.setServerError(err);
            });
        }

        private generateFees() {
            this.requestPending = true;
            const payload = {
                id: this.budgetId,
                queryParams: {
                    generate: this.numberOfFees,
                },
            };
            this.$store.dispatch('generateFees', payload).then(
                (res) => {
                    this.requestPending = false;
                },
            ).catch((err) => {
                this.setServerError(err);
            });
        }

        private delegationName(participant: any) {
            let name = 'Sin confirmar';
            if ( participant.delegation ) {
                name = participant.delegation.name;
            }
            return name;
        }

        private convocationDate(participant: any) {
            let date = 'Sin confirmar';
            if ( participant.initialDate ) {
                date = (this.$options.filters as any).formatDate(participant.initialDate, 'DD/MM/YY');
            }
            return date;
        }

        private getParticipantRequiredImport(participant: any) {
            let requiredImport = ' - ';
            if ( participant.requiredImport ) {
                requiredImport = `${participant.requiredImport} €`;
            }
            return requiredImport;
        }
    }

